// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useCallback, useState } from 'react';
import { useAppDispatch } from 'Hooks';
import { SetProjectIdAction } from 'Features/project/projectActions';
import { GetProjectThunk } from 'Features/project/projectThunk';
import { GetOrdersThunk } from 'Features/order/orderThunk';
import { GetRasterTilesThunk, GetVectorTilesThunk } from 'Features/map/mapCommonThunk';
import { LoadKmlsThunk } from 'Features/kml/kmlThunk';
import { ProjectLoadFailure, projectLoadFailure } from 'Features/project/ProjectNotFound/projectLoadFailure';
import { GetAllAnnotationThunk, LoadPanoFeaturesThunk } from './esriSplitViewerThunk';

// The viewer's project and the data its sidebar and layers read, loaded without waiting for the ArcGIS token. A
// project that is gone or did not load stops the load and is reported as `loadFailure`; `load` runs it again.
export const useViewerProjectLoad = (projectId: string | undefined) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [loadFailure, setLoadFailure] = useState<ProjectLoadFailure | null>(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoadFailure(null);
    setLoading(true);
    try {
      dispatch(SetProjectIdAction(projectId));
      const failure = projectLoadFailure(await dispatch(GetProjectThunk()));
      if (failure) {
        setLoadFailure(failure);
        return;
      }
      await dispatch(GetOrdersThunk(projectId));
      await dispatch(GetAllAnnotationThunk());
      await dispatch(GetRasterTilesThunk());
      await dispatch(GetVectorTilesThunk());
      await dispatch(LoadKmlsThunk());
      await dispatch(LoadPanoFeaturesThunk());
    } catch {
      setLoadFailure('loadFailed');
    } finally {
      setLoading(false);
    }
  }, [dispatch, projectId]);

  return { loading, loadFailure, load };
};

export default useViewerProjectLoad;
